// ChartOfAccounts.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AlertCircle, AlertTriangle, HelpCircle, Filter, ChevronsUpDown, ArrowUpDown, ArrowUp, ArrowDown, Check, RotateCcw } from 'lucide-react';
import { ChartAccount, AccountType, CategoryTemplate } from '../types';
import { categoryOptions, suggestCategory, getCategoryType, getFSType, generateCategorySuggestions } from '../lib/categories/categories';
import SearchableCategorySelect from './categories/SearchableCategorySelect';

interface ChartOfAccountsProps {
  accounts: ChartAccount[];
  onCategoryChange: (accountNumber: string, category: string) => void;
  onBatchCategoryChange?: (updates: Array<{ accountNumber: string, category: string }>) => void;
  suggestionsApplied?: boolean;
  onSuggestionsApplied?: () => void;
  activeTemplate: CategoryTemplate | null;
  /**
   * If true, indicates the ChartOfAccounts tab is currently active. Used to reset sort order on tab activation.
   */
  isActive?: boolean;
  onUploadAdditionalData?: () => void;
}

interface HistoryState {
  accountNumber: string;
  oldCategory: string;
  newCategory: string;
}

interface FilterState {
  value: string[];
  enabled: boolean;
}

type Filters = {
  accountNumber: FilterState;
  name: FilterState;
  category: FilterState;
  suggestedCategory: FilterState;
  type: FilterState;
  statement: FilterState;
};

// Sorting configuration
type SortField = 'accountNumber' | 'name' | 'category' | 'suggestedCategory' | 'type' | 'statement' | 'originalOrder';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

function ComboboxSelect({ 
  value, 
  onChange, 
  options, 
  placeholder,
  onToggle = () => {} 
}: { 
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  placeholder: string;
  onToggle?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside of the component
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onToggle();
    }
  };

  // Filter options based on input
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleSelect = (selectedOption: string) => {
    const newValue = value.includes(selectedOption)
      ? value.filter(v => v !== selectedOption)
      : [...value, selectedOption];
    onChange(newValue);
    setInputValue('');
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <div className="min-h-[36px] w-full border rounded px-2 py-1 text-sm flex flex-wrap gap-1">
          {/* Selected items */}
          {value.map((item) => (
            <span
              key={item}
              className="bg-blue-100 text-blue-800 rounded px-1 flex items-center gap-1"
            >
              {item}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter(v => v !== item));
                }}
                className="hover:text-blue-600 ml-1"
              >
                ×
              </button>
            </span>
          ))}
          
          {/* Input field */}
          <input
            type="text"
            className="flex-1 outline-none min-w-[60px] bg-transparent"
            placeholder={value.length === 0 ? placeholder : ''}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="absolute inset-y-0 right-0 flex items-center pr-2"
        >
          <ChevronsUpDown className="h-4 w-4 text-gray-400" />
        </button>
      </div>
      {isOpen && (
        <div className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg border z-50">
          {filteredOptions.length === 0 ? (
            <div className="relative px-2 py-1.5 text-sm text-gray-500">
              No options found
            </div>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option}
                className="relative flex cursor-pointer select-none items-center px-2 py-1.5 text-sm hover:bg-gray-100"
                onClick={() => handleSelect(option)}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={value.includes(option)}
                    onChange={() => {}}
                    className="rounded border-gray-300"
                  />
                  <span className="truncate">{option}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function ChartOfAccounts({ 
  accounts, 
  onCategoryChange, 
  onBatchCategoryChange,
  suggestionsApplied = false,
  onSuggestionsApplied,
  activeTemplate,
  isActive = false,
  onUploadAdditionalData,
}: ChartOfAccountsProps) {
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [filters, setFilters] = useState<Filters>({
    accountNumber: { value: [], enabled: false },
    name: { value: [], enabled: false },
    category: { value: [], enabled: false },
    suggestedCategory: { value: [], enabled: false },
    type: { value: [], enabled: false },
    statement: { value: [], enabled: false }
  });
  const [activeFilterColumn, setActiveFilterColumn] = useState<keyof Filters | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'originalOrder', direction: 'asc' });
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const prevIsActiveRef = useRef(isActive);

  // Get unique accounts by accountNumber and preserve original order
  const uniqueAccounts = useMemo(() => {
    const accountMap = new Map<string, ChartAccount & { originalOrder: number }>();
    
    accounts.forEach((account, index) => {
      if (!accountMap.has(account.accountNumber)) {
        accountMap.set(account.accountNumber, { ...account, originalOrder: index });
      }
    });
    
    return Array.from(accountMap.values());
  }, [accounts]);

  // Get unique values for filter dropdowns
  const uniqueValues = useMemo(() => {
    const accountNumbers = [...new Set(uniqueAccounts.map(a => a.accountNumber))];
    const names = [...new Set(uniqueAccounts.map(a => a.name))];
    const categories = [...new Set(uniqueAccounts.map(a => {
      const templateCategory = activeTemplate?.categories.find(cat => cat.value === a.category);
      return templateCategory ? templateCategory.label : (a.category || 'N/A');
    }))];
    const suggestedCategories = [...new Set(uniqueAccounts.map(a => {
      const suggested = suggestCategory(a.name, activeTemplate?.categories);
      const templateCategory = activeTemplate?.categories.find(cat => cat.value === suggested);
      return templateCategory ? templateCategory.label : 'N/A';
    }))];
    const types = [...new Set(uniqueAccounts.map(a => 
      a.type ? a.type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'N/A'
    ))];
    const statements = ['Balance Sheet', 'Income Statement'];

    return {
      accountNumber: accountNumbers,
      name: names,
      category: categories,
      suggestedCategory: suggestedCategories,
      type: types,
      statement: statements
    };
  }, [uniqueAccounts, activeTemplate]);
  
  // Function to generate suggestions for all uncategorized accounts
  const generateSuggestions = useCallback(() => {
    const suggestions = generateCategorySuggestions(uniqueAccounts, activeTemplate?.categories);
    
    if (suggestions.length > 0) {
      if (onBatchCategoryChange) {
        onBatchCategoryChange(suggestions);
      } else {
        // Fallback to individual updates if batch update not available
        suggestions.forEach(({ accountNumber, category }) => {
          onCategoryChange(accountNumber, category);
        });
      }
      
      if (onSuggestionsApplied) {
        onSuggestionsApplied();
      }
    }
  }, [uniqueAccounts, activeTemplate?.categories, onBatchCategoryChange, onCategoryChange, onSuggestionsApplied]);
  
  // Handle category changes with history
  const handleCategoryChange = useCallback((accountNumber: string, newCategory: string) => {
    const account = accounts.find(a => a.accountNumber === accountNumber);
    const oldCategory = account?.category || 'N/A';

    // Create new history entry
    const newHistoryEntry: HistoryState = {
      accountNumber,
      oldCategory,
      newCategory
    };

    // Remove any future history entries if we're not at the end
    const newHistory = history.slice(0, currentHistoryIndex + 1);
    
    setHistory([...newHistory, newHistoryEntry]);
    setCurrentHistoryIndex(prev => prev + 1);
    
    onCategoryChange(accountNumber, newCategory);
  }, [accounts, history, currentHistoryIndex, onCategoryChange]);

  // Undo function
  const undo = useCallback(() => {
    if (currentHistoryIndex >= 0) {
      const historyEntry = history[currentHistoryIndex];
      onCategoryChange(historyEntry.accountNumber, historyEntry.oldCategory);
      setCurrentHistoryIndex(prev => prev - 1);
    }
  }, [history, currentHistoryIndex, onCategoryChange]);

  // Redo function
  const redo = useCallback(() => {
    if (currentHistoryIndex < history.length - 1) {
      const historyEntry = history[currentHistoryIndex + 1];
      onCategoryChange(historyEntry.accountNumber, historyEntry.newCategory);
      setCurrentHistoryIndex(prev => prev + 1);
    }
  }, [history, currentHistoryIndex, onCategoryChange]);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) { // metaKey for Mac support
        if (event.key === 'z') {
          event.preventDefault();
          undo();
        } else if (event.key === 'y') {
          event.preventDefault();
          redo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Auto-categorization effect - runs only once when component mounts
  useEffect(() => {
    if (!suggestionsApplied && uniqueAccounts.length > 0) {
      generateSuggestions();
    }
  }, [suggestionsApplied, uniqueAccounts.length, generateSuggestions]);

  // Check if a category is valid in the active template
  const isValidCategory = useCallback((category: string | undefined): boolean => {
    if (!category || category === 'N/A') return false;
    
    // Check if the category exists in the active template
    return !!activeTemplate?.categories.find(cat => cat.value === category);
  }, [activeTemplate]);

  // Apply suggested category
  const applySuggestedCategory = useCallback((accountNumber: string, suggestedCategory: string) => {
    if (suggestedCategory && suggestedCategory !== 'N/A') {
      handleCategoryChange(accountNumber, suggestedCategory);
    }
  }, [handleCategoryChange]);

  const handleFilterChange = (field: keyof typeof filters, value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], value }
    }));
  };

  const handleFilterToggle = (field: keyof typeof filters) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], enabled: !prev[field].enabled }
    }));
  };

  const clearFilters = () => {
    setFilters({
      accountNumber: { value: [], enabled: false },
      name: { value: [], enabled: false },
      category: { value: [], enabled: false },
      suggestedCategory: { value: [], enabled: false },
      type: { value: [], enabled: false },
      statement: { value: [], enabled: false }
    });
    setActiveFilterColumn(null);
  };

  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => {
      if (prevConfig.field === field) {
        // Toggle direction if clicking on the same field
        return { 
          field, 
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' 
        };
      }
      // Default to ascending order for new field
      return { field, direction: 'asc' };
    });
  };

  // Reset to original upload order
  const resetToOriginalOrder = () => {
    setSortConfig({ field: 'originalOrder', direction: 'asc' });
  };

  const renderSortIcon = (field: SortField) => {
    // Add explicit style to ensure fixed dimensions for all icons
    const iconStyle = { width: '16px', height: '16px', minWidth: '16px', minHeight: '16px' };
    
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" style={iconStyle} />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" style={iconStyle} />
      : <ArrowDown className="w-4 h-4 text-blue-600" style={iconStyle} />;
  };

  const hasActiveFilters = Object.values(filters).some(filter => filter.enabled);

  // Apply filters and sorting
  const filteredAndSortedAccounts = useMemo(() => {
    // First apply filters
    const filtered = uniqueAccounts.filter(account => {
      // Get category display name
      const templateCategory = activeTemplate?.categories.find(cat => cat.value === account.category);
      const categoryDisplayName = templateCategory ? templateCategory.label : (account.category || 'N/A');
      
      // Get suggested category display name
      const suggested = suggestCategory(account.name, activeTemplate?.categories);
      const suggestedTemplateCategory = activeTemplate?.categories.find(cat => cat.value === suggested);
      const suggestedCategoryDisplayName = suggestedTemplateCategory ? suggestedTemplateCategory.label : 'N/A';
      
      // Get type display name
      const typeDisplayName = account.type 
        ? account.type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') 
        : 'N/A';
      
      // Get statement type
      const fsType = templateCategory?.fsType || getFSType(account.category || 'N/A');
      const statementDisplayName = fsType === 'balance-sheet' ? 'Balance Sheet' : 'Income Statement';
      
      const matchAccountNumber = !filters.accountNumber.enabled || 
        (filters.accountNumber.value.length === 0 || filters.accountNumber.value.includes(account.accountNumber));
      
      const matchName = !filters.name.enabled || 
        (filters.name.value.length === 0 || filters.name.value.includes(account.name));
      
      const matchCategory = !filters.category.enabled || 
        (filters.category.value.length === 0 || filters.category.value.includes(categoryDisplayName));
      
      const matchSuggestedCategory = !filters.suggestedCategory.enabled || 
        (filters.suggestedCategory.value.length === 0 || filters.suggestedCategory.value.includes(suggestedCategoryDisplayName));
      
      const matchType = !filters.type.enabled || 
        (filters.type.value.length === 0 || filters.type.value.includes(typeDisplayName));
      
      const matchStatement = !filters.statement.enabled || 
        (filters.statement.value.length === 0 || filters.statement.value.includes(statementDisplayName));
      
      return matchAccountNumber && matchName && matchCategory && matchSuggestedCategory && matchType && matchStatement;
    });

    // Then apply sorting
    return [...filtered].sort((a, b) => {
      const { field, direction } = sortConfig;
      
      // Handle originalOrder special case
      if (field === 'originalOrder') {
        return direction === 'asc' 
          ? a.originalOrder - b.originalOrder 
          : b.originalOrder - a.originalOrder;
      }
      
      if (field === 'accountNumber' || field === 'name') {
        // For these fields, we can compare directly
        const aValue = a[field] || '';
        const bValue = b[field] || '';
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      } else if (field === 'category') {
        // For category, get the display name from the template
        const aCategoryObject = activeTemplate?.categories.find(cat => cat.value === a.category);
        const bCategoryObject = activeTemplate?.categories.find(cat => cat.value === b.category);
        
        const aValue = aCategoryObject ? aCategoryObject.label : (a.category || 'N/A');
        const bValue = bCategoryObject ? bCategoryObject.label : (b.category || 'N/A');
        
        return direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (field === 'suggestedCategory') {
        // For suggested category, get the display name from the template
        const aSuggested = suggestCategory(a.name, activeTemplate?.categories);
        const bSuggested = suggestCategory(b.name, activeTemplate?.categories);
        
        const aSuggestedObject = activeTemplate?.categories.find(cat => cat.value === aSuggested);
        const bSuggestedObject = activeTemplate?.categories.find(cat => cat.value === bSuggested);
        
        const aValue = aSuggestedObject ? aSuggestedObject.label : 'N/A';
        const bValue = bSuggestedObject ? bSuggestedObject.label : 'N/A';
        
        return direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (field === 'type') {
        // For type, convert from kebab-case to display format
        const aValue = a.type
          ? a.type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
          : 'N/A';
        const bValue = b.type
          ? b.type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
          : 'N/A';
        
        return direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (field === 'statement') {
        // For statement type, convert from template fsType
        const aFsType = activeTemplate?.categories.find(cat => cat.value === a.category)?.fsType 
          || getFSType(a.category || 'N/A');
        const bFsType = activeTemplate?.categories.find(cat => cat.value === b.category)?.fsType
          || getFSType(b.category || 'N/A');
        
        const aValue = aFsType === 'balance-sheet' ? 'Balance Sheet' : 'Income Statement';
        const bValue = bFsType === 'balance-sheet' ? 'Balance Sheet' : 'Income Statement';
        
        return direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Default case - should not reach here in normal circumstances
      return 0;
    });
  }, [uniqueAccounts, filters, sortConfig, activeTemplate]);

  const FilterPopover = ({ column }: { column: keyof Filters }) => {
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
          setActiveFilterColumn(null);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    if (activeFilterColumn !== column) return null;

    const options = uniqueValues[column as keyof typeof uniqueValues];

    return (
      <div 
        ref={popoverRef}
        className="absolute mt-2 bg-white shadow-lg rounded-lg p-4 z-40 border min-w-[240px]"
        style={{ 
          left: column === 'accountNumber' ? '0' : 'auto',
          right: column === 'statement' ? '0' : 'auto',
          top: '100%'
        }}
      >
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={filters[column].enabled}
            onChange={() => handleFilterToggle(column)}
            className="rounded border-gray-300"
          />
          <ComboboxSelect
            value={filters[column].value}
            onChange={(value) => handleFilterChange(column, value)}
            options={options}
            placeholder={`Filter ${column}...`}
            onToggle={() => handleFilterToggle(column)}
          />
        </div>
      </div>
    );
  };

  // Add custom CSS for sticky table header
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.textContent = `
      .chart-table-wrapper {
        overflow: auto;
        max-height: calc(100vh - 300px);
        border: 1px solid #e5e7eb;
        border-radius: 6px;
      }
      
      .chart-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
      }
      
      .sticky-header-cell {
        position: sticky;
        top: 0;
        z-index: 10;
        background: white;
        box-shadow: 0 1px 0 0 #e5e7eb;
      }
    `;
    document.head.appendChild(styleTag);
    
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  useEffect(() => {
    // Only reset if isActive transitions from false to true
    if (!prevIsActiveRef.current && isActive) {
      setSortConfig({ field: 'originalOrder', direction: 'asc' });
    }
    prevIsActiveRef.current = isActive;
  }, [isActive]);

  return (
    <div>
      {/* Active Template Banner - Styled to match P&L Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
              Active Template:
            </span>
            <span className="text-sm text-blue-800 font-medium">
              {activeTemplate?.name || 'None'}
            </span>
          </div>
          <button 
            onClick={onUploadAdditionalData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            Upload Additional Data
          </button>
        </div>
      </div>

      {/* Instructions for category management */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <HelpCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              To manage category options or change templates, go to the <strong>Categories</strong> tab in the left navigation menu.
              Categories are defined in the active template and affect all financial reports.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-4 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Showing {filteredAndSortedAccounts.length} of {uniqueAccounts.length} accounts
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 flex-shrink-0 mr-2">
              Keyboard shortcuts: Ctrl+Z (Undo), Ctrl+Y (Redo)
            </span>
            <button
              onClick={resetToOriginalOrder}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Upload Order
            </button>
            
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear filters
              </button>
            )}
            {/* 
            <button
              onClick={generateSuggestions}
              className="px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Suggest All N/A Categories
            </button>
            */}
          </div>
        </div>
        
        <div className="chart-table-wrapper" ref={tableContainerRef}>
          <table className="chart-table min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSort('accountNumber')}
                      className="flex items-center gap-1 hover:text-blue-600"
                    >
                      <span>Account Number</span>
                      {renderSortIcon('accountNumber')}
                    </button>
                    <button
                      onClick={() => setActiveFilterColumn(activeFilterColumn === 'accountNumber' ? null : 'accountNumber')}
                      className={`hover:bg-gray-100 p-1 rounded ${
                        filters.accountNumber.enabled ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    >
                      <Filter className="w-4 h-4" />
                    </button>
                  </div>
                  <FilterPopover column="accountNumber" />
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-1 hover:text-blue-600"
                    >
                      <span>Account Name</span>
                      {renderSortIcon('name')}
                    </button>
                    <button
                      onClick={() => setActiveFilterColumn(activeFilterColumn === 'name' ? null : 'name')}
                      className={`hover:bg-gray-100 p-1 rounded ${
                        filters.name.enabled ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    >
                      <Filter className="w-4 h-4" />
                    </button>
                  </div>
                  <FilterPopover column="name" />
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell relative">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSort('category')}
                      className="flex items-center gap-1 hover:text-blue-600"
                    >
                      <span>Selected Category</span>
                      {renderSortIcon('category')}
                    </button>
                    <button
                      onClick={() => setActiveFilterColumn(activeFilterColumn === 'category' ? null : 'category')}
                      className={`hover:bg-gray-100 p-1 rounded ${
                        filters.category.enabled ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    >
                      <Filter className="w-4 h-4" />
                    </button>
                  </div>
                  <FilterPopover column="category" />
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 sticky-header-cell relative bg-blue-50/40">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSort('suggestedCategory')}
                      className="flex items-center gap-1 hover:text-blue-600"
                    >
                      <span>Suggested Category</span>
                      {renderSortIcon('suggestedCategory')}
                    </button>
                    <button
                      onClick={() => setActiveFilterColumn(activeFilterColumn === 'suggestedCategory' ? null : 'suggestedCategory')}
                      className={`hover:bg-gray-100 p-1 rounded ${
                        filters.suggestedCategory.enabled ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    >
                      <Filter className="w-4 h-4" />
                    </button>
                  </div>
                  <FilterPopover column="suggestedCategory" />
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell relative">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSort('type')}
                      className="flex items-center gap-1 hover:text-blue-600"
                    >
                      <span>Category Type</span>
                      {renderSortIcon('type')}
                    </button>
                    <button
                      onClick={() => setActiveFilterColumn(activeFilterColumn === 'type' ? null : 'type')}
                      className={`hover:bg-gray-100 p-1 rounded ${
                        filters.type.enabled ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    >
                      <Filter className="w-4 h-4" />
                    </button>
                  </div>
                  <FilterPopover column="type" />
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell relative">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSort('statement')}
                      className="flex items-center gap-1 hover:text-blue-600"
                    >
                      <span>Statement Type</span>
                      {renderSortIcon('statement')}
                    </button>
                    <button
                      onClick={() => setActiveFilterColumn(activeFilterColumn === 'statement' ? null : 'statement')}
                      className={`hover:bg-gray-100 p-1 rounded ${
                        filters.statement.enabled ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    >
                      <Filter className="w-4 h-4" />
                    </button>
                  </div>
                  <FilterPopover column="statement" />
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                  Warnings
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAndSortedAccounts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <Filter className="h-10 w-10 text-gray-300 mb-2" />
                      <p className="font-medium">No matching accounts found</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or search terms</p>
                      {hasActiveFilters && (
                        <button 
                          onClick={clearFilters}
                          className="mt-3 px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedAccounts.map((account) => {
                  const isUnassigned = !account.category || account.category === 'N/A';
                  const isInvalidCategory = !isUnassigned && !isValidCategory(account.category);
                  
                  // Get category display name
                  const templateCategory = activeTemplate?.categories.find(cat => cat.value === account.category);
                  const categoryDisplayName = templateCategory ? templateCategory.label : (account.category || 'N/A');
                  
                  // Get statement type
                  const fsType = templateCategory?.fsType || getFSType(account.category || 'N/A');
                  
                  return (
                    <tr key={account.accountNumber} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {account.accountNumber}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {account.name}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        <SearchableCategorySelect
                          value={account.category || 'N/A'}
                          onChange={(value) => handleCategoryChange(account.accountNumber, value)}
                          templateCategories={activeTemplate?.categories}
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-sm bg-blue-50/30">
                        {(() => {
                          const suggested = suggestCategory(account.name, activeTemplate?.categories);
                          const templateCategory = activeTemplate?.categories.find(cat => cat.value === suggested);
                          const suggestedDisplayName = templateCategory ? templateCategory.label : 'N/A';
                          const isAlreadySelected = account.category === suggested;
                          
                          return (
                            <div className="flex items-center gap-2">
                              <span className={`text-xs italic ${isAlreadySelected ? 'text-gray-400' : 'text-blue-600'}`}>
                                {suggestedDisplayName}
                              </span>
                              {!isAlreadySelected && suggested !== 'N/A' && (
                                <button
                                  onClick={() => applySuggestedCategory(account.accountNumber, suggested)}
                                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded px-1 py-0.5 text-sm font-medium transition-colors"
                                  title="Apply suggested category"
                                >
                                  →
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {account.type ? 
                          account.type.split('-')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ') 
                          : 'N/A'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${fsType === 'balance-sheet' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                          {fsType === 'balance-sheet' ? 'Balance Sheet' : 'Income Statement'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {isUnassigned && (
                          <div className="flex items-center text-red-600">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            <span>Unassigned</span>
                          </div>
                        )}
                        {isInvalidCategory && (
                          <div className="flex items-center text-red-600">
                            <AlertTriangle className="h-4 w-4 mr-1" />
                            <span>Category not in active template</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
